import { MOMENT_TYPES } from 'utils/constants';

import { MOMENT_TYPE_PRICE_CHANGES_AND_OFFERS } from './momentTypeConstants';

/**
 * Configuration for which date fields to display in General Info section based on moment type
 *
 * This is the single source of truth for date field visibility.
 * To add/remove fields for a moment type, simply update this configuration.
 */

const NOTIFICATION_AND_EFFECTIVE_DATE_CONFIG: MomentTypeDateConfig = {
  dateFields: [
    {
      fieldName: 'customerNotificationDate',
      label: 'CUSTOMER NOTIFICATION DATE',
      type: 'date',
      required: true,
      helpText: 'Date customers are notified of this change'
    },
    {
      fieldName: 'effectiveDate',
      label: 'EFFECTIVE DATE',
      type: 'date',
      required: true,
      helpText: 'Date when the change takes effect'
    }
  ],
  showOnRoadmap: true,
  secondaryDateField: 'effectiveDate'
};

export interface DateFieldConfig {
  fieldName: string;
  label: string;
  type: 'date' | 'month' | 'quarter';
  required: boolean;
  helpText?: string;
}

export interface MomentTypeDateConfig {
  // Date fields to show in General Info
  dateFields: DateFieldConfig[];
  // Other boolean toggles
  showOnRoadmap: boolean;
  // Which date field, if any, the secondaryDateStatus (Active/Completed) tracks.
  // Independent of showOnRoadmap: the status is managed whether or not the
  // additional card is displayed.
  secondaryDateField?: 'releaseDate' | 'effectiveDate';
}

/**
 * Date field visibility configuration by moment type
 *
 * Key = Moment Type value from API
 * Value = Configuration object with date fields and toggles
 */
export const MOMENT_TYPE_DATE_CONFIG: Record<string, MomentTypeDateConfig> = {
  'Product Release with Marketing': {
    dateFields: [
      {
        fieldName: 'targetStart',
        label: 'MARKETING DATE',
        type: 'date',
        required: true,
        helpText: 'Target marketing/announcement date'
      },
      {
        fieldName: 'releaseDate',
        label: 'RELEASE DATE',
        type: 'date',
        required: true,
        helpText: 'Product release date'
      }
    ],
    showOnRoadmap: true,
    secondaryDateField: 'releaseDate'
  },

  'Product Release without Marketing': {
    dateFields: [
      {
        fieldName: 'releaseDate',
        label: 'RELEASE DATE',
        type: 'date',
        required: true,
        helpText: 'Product release date'
      }
    ],
    showOnRoadmap: false // No marketing date, no show on roadmap toggle
  },

  'Marketing Moment': {
    dateFields: [
      {
        fieldName: 'targetStart',
        label: 'MARKETING DATE',
        type: 'date',
        required: true,
        helpText: 'Target marketing/announcement date'
      }
      // No release date for Marketing Moment type
    ],
    showOnRoadmap: false // No show on roadmap toggle, no release date
  },

  'Offer, Price Change, or Program': {
    dateFields: [
      {
        fieldName: 'targetStart',
        label: 'MARKETING DATE',
        type: 'date',
        required: true,
        helpText: 'Target announcement/start date'
      },
      {
        fieldName: 'effectiveDate',
        label: 'EFFECTIVE DATE',
        type: 'date',
        required: true,
        helpText: 'Date when offer/price change takes effect'
      }
    ],
    showOnRoadmap: true,
    secondaryDateField: 'effectiveDate'
  },

  [MOMENT_TYPE_PRICE_CHANGES_AND_OFFERS]:
    NOTIFICATION_AND_EFFECTIVE_DATE_CONFIG,

  [MOMENT_TYPES.END_OF_LIFE]: NOTIFICATION_AND_EFFECTIVE_DATE_CONFIG,

  'New 1.0 Release': {
    dateFields: [
      {
        fieldName: 'targetStart',
        label: 'MARKETING DATE',
        type: 'date',
        required: true,
        helpText: 'Target marketing/announcement date'
      },
      {
        fieldName: 'releaseDate',
        label: 'RELEASE DATE',
        type: 'date',
        required: true,
        helpText: 'Product release date'
      }
    ],
    showOnRoadmap: true,
    secondaryDateField: 'releaseDate'
  },

  // Additional moment types with default configurations
  'Key Dates': {
    dateFields: [
      {
        fieldName: 'targetStart',
        label: 'KEY DATE',
        type: 'date',
        required: true,
        helpText: 'Important date to track'
      }
    ],
    showOnRoadmap: false
  },

  Events: {
    dateFields: [
      {
        fieldName: 'targetStart',
        label: 'EVENT DATE',
        type: 'date',
        required: true,
        helpText: 'Date of the event'
      }
    ],
    showOnRoadmap: false
  }
};

/**
 * Helper function to get date fields for a specific moment type
 * @param momentType - The moment type string (e.g., "Product Release with Marketing")
 * @returns Array of date field configurations or empty array if moment type not found
 */
export const getDateFieldsForMomentType = (
  momentType: string
): DateFieldConfig[] => {
  const config = MOMENT_TYPE_DATE_CONFIG[momentType];
  return config?.dateFields || [];
};

/**
 * Helper function to check if a specific date field should be visible for a moment type
 * @param momentType - The moment type string
 * @param fieldName - The field name (e.g., "releaseDate", "targetStart")
 * @returns true if field should be visible, false otherwise
 */
export const shouldShowDateField = (
  momentType: string,
  fieldName: string
): boolean => {
  const config = MOMENT_TYPE_DATE_CONFIG[momentType];
  if (!config) return false;

  return config.dateFields.some(field => field.fieldName === fieldName);
};

/**
 * Helper function to check if "Show on Roadmap" toggle should be visible
 * @param momentType - The moment type string
 * @returns true if toggle should be visible, false otherwise
 */
export const shouldShowRoadmapToggle = (momentType: string): boolean => {
  const config = MOMENT_TYPE_DATE_CONFIG[momentType];
  return config?.showOnRoadmap ?? false;
};

/**
 * Whether a moment type has a secondary date whose status the user can manage.
 *
 * Deliberately separate from `shouldShowRoadmapToggle`: that answers "can this type
 * display an additional card?", which is presentation. This answers "does this type
 * have a secondary date at all?", which is what makes the status meaningful. The two
 * type lists coincide today but the questions are not the same one.
 * @param momentType - The moment type string
 * @returns true if the type has a secondary date status, false otherwise
 */
export const hasSecondaryDateStatus = (momentType: string): boolean =>
  getSecondaryDateField(momentType) !== undefined;

/**
 * Which date field the secondary status tracks for a moment type.
 * @param momentType - The moment type string
 * @returns 'releaseDate', 'effectiveDate', or undefined when the type has no secondary date
 */
export const getSecondaryDateField = (
  momentType: string
): 'releaseDate' | 'effectiveDate' | undefined =>
  MOMENT_TYPE_DATE_CONFIG[momentType]?.secondaryDateField;

/**
 * The label of a moment type's primary date — the first configured date field.
 * @param momentType - The moment type string
 * @returns The label, or undefined when the type has no date fields
 */
export const getPrimaryDateLabel = (momentType: string): string | undefined =>
  MOMENT_TYPE_DATE_CONFIG[momentType]?.dateFields?.[0]?.label;

/**
 * The label of a moment type's secondary date.
 * @param momentType - The moment type string
 * @returns The label, or undefined when the type has no secondary date
 */
export const getSecondaryDateLabel = (
  momentType: string
): string | undefined => {
  const config = MOMENT_TYPE_DATE_CONFIG[momentType];
  const secondaryDateField = config?.secondaryDateField;
  if (!config || !secondaryDateField) return undefined;

  return config.dateFields.find(f => f.fieldName === secondaryDateField)?.label;
};

/**
 * Helper function to check if a date field is required for a moment type
 * @param momentType - The moment type string
 * @param fieldName - The field name
 * @returns true if field is required, false otherwise
 */
export const isDateFieldRequired = (
  momentType: string,
  fieldName: string
): boolean => {
  const config = MOMENT_TYPE_DATE_CONFIG[momentType];
  if (!config) return false;

  const field = config.dateFields.find(f => f.fieldName === fieldName);
  return field?.required ?? false;
};

/**
 * Helper function to get the label for a date field
 * @param momentType - The moment type string
 * @param fieldName - The field name
 * @returns The label string or undefined if not found
 */
export const getDateFieldLabel = (
  momentType: string,
  fieldName: string
): string | undefined => {
  const config = MOMENT_TYPE_DATE_CONFIG[momentType];
  if (!config) return undefined;

  const field = config.dateFields.find(f => f.fieldName === fieldName);
  return field?.label;
};

/**
 * Default configuration for unknown moment types
 * Shows common date fields that work for most types
 */
export const DEFAULT_DATE_CONFIG: MomentTypeDateConfig = {
  dateFields: [
    {
      fieldName: 'targetStart',
      label: 'TARGET DATE',
      type: 'date',
      required: false
    },
    {
      fieldName: 'releaseDate',
      label: 'RELEASE DATE',
      type: 'date',
      required: false
    }
  ],
  showOnRoadmap: false
};

/**
 * Get configuration for a moment type, with fallback to default
 * @param momentType - The moment type string
 * @returns Configuration object
 */
export const getConfigForMomentType = (
  momentType: string
): MomentTypeDateConfig => {
  return MOMENT_TYPE_DATE_CONFIG[momentType] || DEFAULT_DATE_CONFIG;
};
